import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LOCALES, type Locale } from '@scoreboard/shared';
import { BrandMark, Loading } from '@scoreboard/ui-web';
import { setLocale } from '@/lib/i18n';
import { useSession } from '@/lib/session';
import { useTheme } from '@/lib/theme';
import LoginRoute from '@/routes/login';
import AuthVerifyRoute from '@/routes/auth-verify';

// Code-splitting par route : l'écran de scoring est le chunk prioritaire, la liste et
// le formulaire sont chargés à la demande (doc 03 §8).
const GamesListRoute = lazy(() => import('@/routes/games-list'));
const GameNewRoute = lazy(() => import('@/routes/game-new'));
const GameScoringRoute = lazy(() => import('@/routes/game-scoring'));
const AccountRoute = lazy(() => import('@/routes/account'));

function AppBar(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const { status, account, logout } = useSession();
  return (
    <header className="appbar">
      {/* Pastille + nom écrit : la marque signe la barre sans redire « Scorofun » à
          un lecteur d'écran (BrandMark est `aria-hidden`). */}
      <Link className="appbar__brand" to="/games">
        <BrandMark size={26} />
        {t('common.appName')}
      </Link>
      <span className="grow" />
      <label className="sr-only" htmlFor="locale-switch">
        {t('common.language')}
      </label>
      <select
        id="locale-switch"
        value={i18n.language}
        onChange={(e) => setLocale(e.target.value as Locale)}
      >
        {LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {locale.toUpperCase()}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn--ghost"
        aria-pressed={theme === 'light'}
        onClick={toggle}
      >
        {t('common.theme')}: {t(theme === 'dark' ? 'common.themeDark' : 'common.themeLight')}
      </button>
      {status === 'authenticated' ? (
        <Link className="btn btn--ghost" to="/compte">
          {t('billing.title')}
        </Link>
      ) : null}
      {status === 'authenticated' ? (
        <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
          {t('common.logout')}
          {account?.email !== null && account?.email !== undefined ? (
            <span className="sr-only"> ({account.email})</span>
          ) : null}
        </button>
      ) : null}
    </header>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }): React.ReactElement {
  const { status } = useSession();
  const location = useLocation();
  if (status === 'loading') {
    return (
      <main className="page" id="main">
        <Loading />
      </main>
    );
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

export function App(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t('common.skipToContent')}
      </a>
      <AppBar />
      <Suspense
        fallback={
          <main className="page" id="main">
            <Loading />
          </main>
        }
      >
        <Routes>
          <Route path="/" element={<Navigate to="/games" replace />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/auth" element={<AuthVerifyRoute />} />
          <Route
            path="/games"
            element={
              <RequireAuth>
                <GamesListRoute />
              </RequireAuth>
            }
          />
          <Route
            path="/games/new"
            element={
              <RequireAuth>
                <GameNewRoute />
              </RequireAuth>
            }
          />
          <Route
            path="/games/:gameId"
            element={
              <RequireAuth>
                <GameScoringRoute />
              </RequireAuth>
            }
          />
          <Route
            path="/compte"
            element={
              <RequireAuth>
                <AccountRoute />
              </RequireAuth>
            }
          />
          <Route
            path="*"
            element={
              <main className="page stack" id="main">
                <h1>{t('errors.notFound')}</h1>
                <Link className="btn btn--primary" to="/games">
                  {t('games.title')}
                </Link>
              </main>
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
}
